import * as THREE from 'three';
import {
  cameraPosition,
  fog as tslFog,
  positionView,
  positionWorld,
  uniform,
} from 'three/tsl';

// TSL's exported types treat `uniform()` as returning a bare `UniformNode`
// without the chainable math operators (`.mul`, `.mix`, `.normalize`, ...),
// even though the runtime extends every node with them. Cast the helper to
// `any` so the TSL graph reads naturally without per-call type assertions —
// matching the pattern Sky.ts and Terrain.ts already use.
const makeNodeUniform = uniform as unknown as (
  value: unknown,
) => ReturnType<typeof uniform> & Record<string, any>;
import type { StyleConfig } from './StyleConfig';
import type { ResolvedStyle } from './OverrideResolver';
import type { RendererMode } from './Renderer';

export interface AerialUniforms {
  uSunDirWorld: { value: THREE.Vector3 };
  uHorizonColor: { value: THREE.Color };
  uZenithColor: { value: THREE.Color };
  uFogWarmTint: { value: THREE.Color };
  uFogCoolTint: { value: THREE.Color };
  uInscatterStrength: { value: number };
  uInscatterPower: { value: number };
  uAerialFogDensity: { value: number };
}

export interface AerialTintInputs {
  viewDir: THREE.Vector3;
  sunDir: THREE.Vector3;
  horizonColor: THREE.Color;
  zenithColor: THREE.Color;
  warmTint: THREE.Color;
  coolTint: THREE.Color;
  inscatterStrength: number;
  inscatterPower: number;
}

type AerialMaterial = THREE.Material & {
  fog?: boolean;
  onBeforeCompile: THREE.Material['onBeforeCompile'];
  customProgramCacheKey?: () => string;
};

export const SHARED_AERIAL_UNIFORMS: AerialUniforms = {
  uSunDirWorld: { value: new THREE.Vector3(0.3, 0.7, 0.6).normalize() },
  uHorizonColor: { value: new THREE.Color('#ffffff') },
  uZenithColor: { value: new THREE.Color('#ffffff') },
  uFogWarmTint: { value: new THREE.Color('#ffd9a6') },
  uFogCoolTint: { value: new THREE.Color('#88a8c4') },
  uInscatterStrength: { value: 0.6 },
  uInscatterPower: { value: 6 },
  uAerialFogDensity: { value: 0.0012 },
};

export function applyAerialPerspectiveStyle(style: StyleConfig | ResolvedStyle): void {
  SHARED_AERIAL_UNIFORMS.uSunDirWorld.value
    .set(style.sky.sunDir.x, style.sky.sunDir.y, style.sky.sunDir.z)
    .normalize();
  SHARED_AERIAL_UNIFORMS.uHorizonColor.value.set(style.sky.horizonHex);
  SHARED_AERIAL_UNIFORMS.uZenithColor.value.set(style.sky.zenithHex);
  SHARED_AERIAL_UNIFORMS.uFogWarmTint.value.set(style.fog.warmTintHex);
  SHARED_AERIAL_UNIFORMS.uFogCoolTint.value.set(style.fog.coolTintHex);
  SHARED_AERIAL_UNIFORMS.uInscatterStrength.value = style.fog.inscatterStrength;
  SHARED_AERIAL_UNIFORMS.uInscatterPower.value = style.fog.inscatterPower;
  SHARED_AERIAL_UNIFORMS.uAerialFogDensity.value = style.fog.density;
  syncWebGPUAerialFogUniforms();
}

type AerialUniformNode = ReturnType<typeof makeNodeUniform>;

interface WebGPUAerialFogUniforms {
  sunDir: AerialUniformNode;
  horizonColor: AerialUniformNode;
  zenithColor: AerialUniformNode;
  warmTint: AerialUniformNode;
  coolTint: AerialUniformNode;
  inscatterStrength: AerialUniformNode;
  inscatterPower: AerialUniformNode;
  density: AerialUniformNode;
}

let webgpuAerialFogUniforms: WebGPUAerialFogUniforms | null = null;

function colorToVec3(color: THREE.Color): THREE.Vector3 {
  return new THREE.Vector3(color.r, color.g, color.b);
}

function setVec3FromColor(target: THREE.Vector3, source: THREE.Color): void {
  target.set(source.r, source.g, source.b);
}

function syncWebGPUAerialFogUniforms(): void {
  const u = webgpuAerialFogUniforms;
  if (!u) return;
  (u.sunDir.value as THREE.Vector3).copy(SHARED_AERIAL_UNIFORMS.uSunDirWorld.value);
  setVec3FromColor(u.horizonColor.value as THREE.Vector3, SHARED_AERIAL_UNIFORMS.uHorizonColor.value);
  setVec3FromColor(u.zenithColor.value as THREE.Vector3, SHARED_AERIAL_UNIFORMS.uZenithColor.value);
  setVec3FromColor(u.warmTint.value as THREE.Vector3, SHARED_AERIAL_UNIFORMS.uFogWarmTint.value);
  setVec3FromColor(u.coolTint.value as THREE.Vector3, SHARED_AERIAL_UNIFORMS.uFogCoolTint.value);
  u.inscatterStrength.value = SHARED_AERIAL_UNIFORMS.uInscatterStrength.value;
  u.inscatterPower.value = Math.max(1, SHARED_AERIAL_UNIFORMS.uInscatterPower.value);
  u.density.value = SHARED_AERIAL_UNIFORMS.uAerialFogDensity.value;
}

/**
 * Install a TSL `scene.fogNode` that mirrors the WebGL `installAerialPerspective`
 * onBeforeCompile shader: distance-based exponential fog whose colour is built
 * from the sky horizon → zenith vertical band, then biased toward warm/cool
 * inscatter when the view direction faces the sun.
 *
 * On the WebGPU backend the WebGL onBeforeCompile path can't run, so without
 * this the scene falls back to plain `THREE.FogExp2` with `style.fog.colorHex`,
 * which renders as flat brown haze that buries the sky. `scene.fogNode`
 * overrides `scene.fog` for materials with `fog: true` and is automatically
 * skipped on the sky dome (`fog: false`).
 *
 * Uniforms are wired to `SHARED_AERIAL_UNIFORMS`, so the existing
 * `applyAerialPerspectiveStyle` call updates both the WebGL and WebGPU paths.
 */
export function installWebGPUAerialFogNode(scene: THREE.Scene): void {
  if (webgpuAerialFogUniforms) return;
  const u: WebGPUAerialFogUniforms = {
    sunDir: makeNodeUniform(SHARED_AERIAL_UNIFORMS.uSunDirWorld.value.clone()),
    horizonColor: makeNodeUniform(colorToVec3(SHARED_AERIAL_UNIFORMS.uHorizonColor.value)),
    zenithColor: makeNodeUniform(colorToVec3(SHARED_AERIAL_UNIFORMS.uZenithColor.value)),
    warmTint: makeNodeUniform(colorToVec3(SHARED_AERIAL_UNIFORMS.uFogWarmTint.value)),
    coolTint: makeNodeUniform(colorToVec3(SHARED_AERIAL_UNIFORMS.uFogCoolTint.value)),
    inscatterStrength: makeNodeUniform(SHARED_AERIAL_UNIFORMS.uInscatterStrength.value),
    inscatterPower: makeNodeUniform(Math.max(1, SHARED_AERIAL_UNIFORMS.uInscatterPower.value)),
    density: makeNodeUniform(SHARED_AERIAL_UNIFORMS.uAerialFogDensity.value),
  };
  webgpuAerialFogUniforms = u;

  const viewDir = positionWorld.sub(cameraPosition).normalize();
  const upBlend = viewDir.y.smoothstep(-0.1, 0.6);
  const dirFog = u.horizonColor.mix(u.zenithColor, upBlend);
  // TSL `.pow()` is typed `Node<'float'>` but `uniform()` returns a generic
  // node — cast to satisfy the signature without losing the runtime binding.
  const sunFacing = viewDir.dot(u.sunDir.normalize()).max(0).pow(u.inscatterPower as any);
  const inscatterTint = u.coolTint.mix(u.warmTint, sunFacing);
  const fogColor = dirFog.mix(
    inscatterTint,
    u.inscatterStrength.mul(sunFacing).clamp(0, 1),
  );

  // FogExp2 parity: factor = 1 - exp(-(density × depth)²). The bundled TSL
  // `densityFogFactor` is FogExp (linear in density), which produces noticeably
  // thinner haze than the WebGL `THREE.FogExp2(colour, density)` the WebGL
  // path uses — visible side-by-side as a clear horizon under WebGPU vs the
  // dust-storm orange wall under WebGL. Compute the squared form by hand.
  // WebGL FogExp2 uses `vFogDepth = -mvPosition.z` (camera looks down -Z),
  // so the depth is the absolute view-space Z of the fragment.
  const depth = positionView.z.abs();
  const k = u.density.mul(depth);
  const factor = k.mul(k).negate().exp().oneMinus();
  (scene as THREE.Scene & { fogNode?: unknown }).fogNode = tslFog(fogColor, factor);
}

export function aerialPerspectiveTint(inputs: AerialTintInputs): THREE.Color {
  const viewDir = inputs.viewDir.clone().normalize();
  const sunDir = inputs.sunDir.clone().normalize();
  const upBlend = smoothstep(-0.1, 0.6, viewDir.y);
  const dirFog = inputs.horizonColor.clone().lerp(inputs.zenithColor, upBlend);
  const sunFacing = Math.pow(Math.max(0, viewDir.dot(sunDir)), Math.max(1, inputs.inscatterPower));
  const inscatterTint = inputs.coolTint.clone().lerp(inputs.warmTint, sunFacing);
  return dirFog.lerp(inscatterTint, THREE.MathUtils.clamp(inputs.inscatterStrength * sunFacing, 0, 1));
}

export function installAerialPerspective(
  material: THREE.Material | null | undefined,
  rendererMode: RendererMode = 'webgl2',
): void {
  if (!material) return;
  const mat = material as AerialMaterial;
  mat.userData ??= {};
  if (mat.userData.aerialPerspectiveInstalled) return;
  mat.userData.aerialPerspectiveInstalled = true;
  if ('fog' in mat && !(mat instanceof THREE.ShaderMaterial)) mat.fog = true;
  if (rendererMode === 'webgpu') {
    mat.userData.aerialPerspectiveWebGPUFallback = true;
    mat.needsUpdate = true;
    return;
  }

  const prev = typeof mat.onBeforeCompile === 'function' ? mat.onBeforeCompile : undefined;
  mat.onBeforeCompile = (shader, renderer) => {
    prev?.(shader, renderer);
    Object.assign(shader.uniforms, SHARED_AERIAL_UNIFORMS);
    shader.vertexShader = ensureAerialWorldPosition(shader.vertexShader);
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
varying vec3 vAerialWorldPosition;
uniform vec3 uSunDirWorld;
uniform vec3 uHorizonColor;
uniform vec3 uZenithColor;
uniform vec3 uFogWarmTint;
uniform vec3 uFogCoolTint;
uniform float uInscatterStrength;
uniform float uInscatterPower;
uniform float uAerialFogDensity;`,
      )
      .replace('#include <fog_fragment>', AERIAL_FOG_FRAGMENT);
  };
  // Without a stable cache key, every material that gets this onBeforeCompile
  // produces a unique compiled program even though the injected GLSL is
  // identical. Fixing the key lets Three.js dedupe the program across all
  // materials, eliminating the synchronous compile stall when a new material
  // first becomes visible.
  const prevCacheKey = mat.customProgramCacheKey?.bind(mat);
  mat.customProgramCacheKey = () => `${prevCacheKey ? prevCacheKey() : ''}|aerial-v1`;
  mat.needsUpdate = true;
}

export function installAerialPerspectiveForObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    const mesh = child as THREE.Object3D & { material?: THREE.Material | Array<THREE.Material | null> | null };
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) installAerialPerspective(material);
  });
}

export function aerialPerspectiveFragmentExpression(viewDirExpression: string): string {
  return `
vec3 aerialViewDir = normalize(${viewDirExpression});
vec3 aerialDirFog = mix(uHorizonColor, uZenithColor, smoothstep(-0.1, 0.6, aerialViewDir.y));
float aerialSunFacing = pow(max(0.0, dot(aerialViewDir, normalize(uSunDirWorld))), uInscatterPower);
vec3 aerialInscatterTint = mix(uFogCoolTint, uFogWarmTint, aerialSunFacing);
vec3 aerialFogColor = mix(aerialDirFog, aerialInscatterTint, uInscatterStrength * aerialSunFacing);
  `.trim();
}

function ensureAerialWorldPosition(vertexShader: string): string {
  let next = vertexShader;
  if (!next.includes('varying vec3 vAerialWorldPosition;')) {
    next = next.replace('#include <common>', '#include <common>\nvarying vec3 vAerialWorldPosition;');
  }
  if (next.includes('vAerialWorldPosition = (modelMatrix * vec4(transformed, 1.0)).xyz;')) return next;
  // The `<worldpos_vertex>` chunk only declares `worldPosition` when one of
  // USE_ENVMAP / DISTANCE / USE_SHADOWMAP / USE_TRANSMISSION / spotlight
  // coords is set. MeshBasicMaterial with fog hits none of those, so reading
  // `worldPosition.xyz` there fails to compile. Recompute world-space from
  // `transformed` ourselves so it's always defined.
  return next.replace(
    '#include <worldpos_vertex>',
    `#include <worldpos_vertex>
vAerialWorldPosition = (modelMatrix * vec4(transformed, 1.0)).xyz;`,
  );
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = THREE.MathUtils.clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

const AERIAL_FOG_FRAGMENT = `
#ifdef USE_FOG
  vec3 vAerialView = normalize(vAerialWorldPosition - cameraPosition);
  ${aerialPerspectiveFragmentExpression('vAerialView')}
  #ifdef FOG_EXP2
    float fogFactor = 1.0 - exp( - fogDensity * fogDensity * vFogDepth * vFogDepth );
  #else
    float fogFactor = smoothstep( fogNear, fogFar, vFogDepth );
  #endif
  gl_FragColor.rgb = mix(gl_FragColor.rgb, aerialFogColor, fogFactor);
#endif
`;
